import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllMakes, getModelsByMake, getProducts } from '../api';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { DrawerTitle } from './ui/drawer';
import { Wrench, X, Info } from 'lucide-react';

export default function GarageDrawer({ onClose }) {
  const navigate = useNavigate();
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);

  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');

  useEffect(() => {
    getAllMakes().then(data => setMakes(data));
  }, []);

  useEffect(() => {
    if (make) {
      getModelsByMake(make).then(data => {
        if (!data || !Array.isArray(data)) {
          setModels([]);
          return;
        }
        const sorted = [...data].sort((a, b) => (a.name || a).localeCompare(b.name || b));
        setModels(sorted);
      }).catch(() => {
        setModels([]);
      });
    } else {
      setModels([]);
    }
    setModel('');
    setYear('');
  }, [make]);

  useEffect(() => {
    if (make && model) {
      getProducts({ make, model }).then(data => {
        if (!data.products || data.products.length === 0) {
          setYears([]);
          return;
        }
        const uniqueYears = [...new Set(
          data.products
            .flatMap(p => (p.fitment || []).flatMap(f => [f.yearFrom, f.yearTo]))
            .filter(y => y)
        )];
        const sorted = [...new Set(uniqueYears)].sort((a, b) => a - b);
        setYears(sorted);
      }).catch(err => {
        console.error('Failed to fetch products for garage:', err);
        setYears([]);
      });
    } else {
      setYears([]);
    }
    setYear('');
  }, [make, model]);

  const handleSearch = () => {
    const params = {};
    if (make) params.make = make;
    if (model) params.model = model;
    if (year) params.year = year;
    onClose();
    navigate({ pathname: '/shop', search: new URLSearchParams(params).toString() });
  };

  const handleReset = () => {
    setMake(''); setModel(''); setYear('');
    onClose();
    navigate('/shop');
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 pt-4">
        <div className="flex items-center gap-2">
          <Wrench className="h-5 w-5 text-foreground" />
          <DrawerTitle>Your Garage</DrawerTitle>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="px-4 py-4 space-y-4">
        <div className="flex items-center gap-2">
          <p className="text-sm text-muted-foreground">
            Select your motorcycle to find all compatible parts.
          </p>
          <Info className="h-4 w-4 text-muted-foreground" />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Select Your Motorcycle</label>
          <div className="space-y-3">
            <Select value={make} onValueChange={setMake}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="1 — Make" />
              </SelectTrigger>
              <SelectContent>
                {makes.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={model} onValueChange={setModel} disabled={models.length === 0}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={models.length === 0 ? "Select a make first" : "2 — Select model"} />
              </SelectTrigger>
              <SelectContent>
                {models.map((m) => (
                  <SelectItem key={m._id} value={m.name}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={year} onValueChange={setYear} disabled={years.length === 0}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="3 — Select year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <Button onClick={handleSearch} className="flex-1 bg-foreground text-background hover:bg-muted">
            Check Garage
          </Button>
          <Button variant="outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
}